// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import React, { useCallback } from 'react';
import { ProjectVersion } from 'model/ProjectMetaMessageExtensions';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';

import { Checkbox, Table } from '@tls/treact-ui';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

function getHeaderSelectionState(selectedCount: number, total: number) {
  if (selectedCount === 0) return 'unselected';
  if (selectedCount === total) return 'selected';
  return 'mixed';
}

export type Props = {
  versions: ProjectVersion[];
  selected: ProjectVersion[];
  resetAll: () => void;
  onClick: (version: ProjectVersion) => () => void;
  disableUnselected: boolean;
  loading: boolean;
};

export default function VersionSelectionTable({
  versions,
  selected,
  resetAll,
  disableUnselected,
  onClick,
  loading,
}: Readonly<Props>) {
  const onSortBy = useCallback((array: ProjectVersion[]) => sortBy(array, ['major', 'minor', 'patch']), []);

  const { t } = useTranslation();

  const headerSelectionState = getHeaderSelectionState(selected.length, versions.length);

  return (
    <Table list={versions}>
      {versionList => (
        <>
          <Table.Header>
            <Table.HeaderRow>
              <Table.HeaderCell width={'3.5rem'} alignment='CENTER' noIndent>
                <Checkbox
                  asAtom
                  value={headerSelectionState}
                  onValueChange={resetAll}
                  disabled={selected.length === 0}
                  data-testid='evaluation-version-reset-checkbox'
                />
              </Table.HeaderCell>
              <Table.HeaderSortCell
                width={'1'}
                sortKey='version'
                sortFn={onSortBy}
                noIndent
                data-testid='evaluation-version-sort-button'
              >
                {t('project.evaluation.versions.title')}
              </Table.HeaderSortCell>
            </Table.HeaderRow>
          </Table.Header>
          <Table.ScrollingBody scrollViewProps={nativeScrollViewProps}>
            {loading && <TestableLoader label='' data-testid='evaluation-version-loader' />}
            {versionList.map((versionString, index) => {
              const isSelected = selected.findIndex(s => s === versionString) > -1;
              const disabled = disableUnselected && !isSelected;
              return (
                <Table.Row
                  className={isSelected ? 'selected' : ''}
                  key={versionString}
                  index={index}
                  onClick={onClick(versionString)}
                  disabled={disabled}
                  data-testid={`evaluation-version-${versionString}-row`}
                >
                  <Table.Cell width={'3.5rem'} alignment='CENTER' noIndent>
                    <Checkbox
                      value={isSelected ? 'selected' : 'unselected'}
                      onValueChange={onClick(versionString)}
                      asAtom
                      disabled={disabled}
                      data-testid={`evaluation-version-${versionString}-checkbox`}
                    />
                  </Table.Cell>
                  <Table.Cell width={'1'}>{t(versionString ?? '')}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.ScrollingBody>
        </>
      )}
    </Table>
  );
}
